import { Injectable } from '@angular/core';
import { NavigationEnd, Router, Event, NavigationStart } from '@angular/router';
import { DateTime } from 'luxon';
import { BehaviorSubject, map } from 'rxjs';

export interface HistoryItem {
  url: string;
  label: string;
}

/** Service that provides automatic navigation functionality. */
@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private readonly MAX_HISTORY = 30;
  private readonly _locationHistory = new BehaviorSubject<HistoryItem[]>(
    this.getHistoryFromStorage(),
  );

  backLocation$ = this._locationHistory.pipe(
    map((locationHistory) => {
      if (locationHistory.length > 0) {
        return locationHistory[locationHistory.length - 1];
      }
      return null;
    }),
  );

  // Flag to detect when back navigation initiated. Disables pushing current location onto history stack.
  private _isBack = false;
  // Disable pushing onto history stack during app load. Otherwise current screen gets added to history.
  private _isAppStart = true;

  constructor(private router: Router) {
    this.router.events.subscribe((event: Event) => {
      this.handleLocationHistoryEvent(event);
      this.saveLastUrl(event);
    });
  }

  /** Update location history based on event. */
  private handleLocationHistoryEvent(event: Event): void {
    if (event instanceof NavigationStart) {
      if (this._isBack) {
        this._isBack = false;
        return;
      }

      const prevLocation = location.pathname;

      if (prevLocation.startsWith('/calendar')) {
        this.pushLocationHistory({
          url: prevLocation,
          label: 'Calendar',
        });
      } else if (prevLocation.startsWith('/sequence')) {
        if (prevLocation.includes('/flight')) {
          this.pushLocationHistory({
            url: prevLocation,
            label: this.getFlightLabel(prevLocation),
          });
        } else {
          this.pushLocationHistory({
            url: prevLocation,
            label: this.getSequenceLabel(prevLocation),
          });
        }
      } else if (prevLocation.startsWith('/search')) {
        this.pushLocationHistory({
          url: prevLocation,
          label: 'Search',
        });
      }
    }
  }

  /** Create sequence label from url. */
  private getSequenceLabel(url: string): string {
    const params = url.split('/');
    // console.log(params, params.length);

    if (params.length !== 5) {
      return 'Sequence';
    }

    const seqNumber = params[2];

    return `Seq ${seqNumber}`;
  }

  /** Extract flight info from URL for back label. */
  private getFlightLabel(url: string): string {
    const params = url.split('/');

    if (params.length !== 10) {
      return '';
    }

    const flightNum = parseInt(params[6]); // Remove leading zeroes by converting to int.
    const dep = params[7].toUpperCase();
    const arr = params[8].toUpperCase();

    return `Flight ${flightNum} ${dep}-${arr}`;
  }

  /** Saves URL to local storage to be retrieved upon initial app load. */
  private saveLastUrl(event: Event): void {
    if (event instanceof NavigationEnd) {
      if (
        event.url.startsWith('/calendar') ||
        event.url.startsWith('/sequence')
      ) {
        localStorage.setItem('lastUrl', event.url);
        localStorage.setItem('lastUrlTime', DateTime.now().toUTC().toISO());
      }
    }
  }

  private getHistoryFromStorage(): HistoryItem[] {
    const history = localStorage.getItem('locationHistory');

    if (history) {
      return JSON.parse(history) as HistoryItem[];
    }

    return [];
  }

  private pushLocationHistory(historyItem: HistoryItem): void {
    if (this._isAppStart) {
      this._isAppStart = false;
      return;
    }

    const locationHistory = [...this._locationHistory.getValue()];

    // Don't push the same URL multiple times in a row.
    if (locationHistory.length > 0) {
      const lastItem = locationHistory[locationHistory.length - 1];
      if (lastItem.url === historyItem.url) {
        return;
      }
    }

    locationHistory.push(historyItem);
    this.setLocationHistory(locationHistory);
  }

  private setLocationHistory(locationHistory: HistoryItem[]): void {
    // Limit history
    if (locationHistory.length > this.MAX_HISTORY) {
      // Remove bottom of stack.
      locationHistory.splice(0, 1);
    }

    localStorage.setItem('locationHistory', JSON.stringify(locationHistory));
    this._locationHistory.next(locationHistory);
  }

  /**
   * Navigate back in location history. This is a custom implementation that provides extra information
   * and ignores unnecessary urls like auth.
   */
  back(): void {
    this._isBack = true;
    const locationHistory = [...this._locationHistory.getValue()];
    const historyItem = locationHistory.pop();
    this.setLocationHistory(locationHistory);

    if (historyItem) {
      this.router.navigate([historyItem.url]);
    }
  }
}
